<script setup>
import { ref, watch, nextTick } from 'vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import IconPicker from './IconPicker.vue';

const props = defineProps({
  modelValue: {
    type: [Array, String],
    default: () => [],
  },
  fields: {
    type: Array,
    required: true,
    // Format: [{ key: 'label', label: 'Label', type: 'text', placeholder: '...' }]
  },
  addButtonLabel: {
    type: String,
    default: 'Ajouter un élément',
  },
  titleField: {
    type: String,
    default: 'label',
  },
});

const emit = defineEmits(['update:modelValue']);

const items = ref([]);
const isUpdating = ref(false);

// Generate a simple unique ID for keys
const getLocalId = () => Math.random().toString(36).substr(2, 9);

watch(
  () => props.modelValue,
  newVal => {
    // Skip update if we're currently updating to prevent focus loss
    if (isUpdating.value) return;

    let parsedItems = [];
    if (typeof newVal === 'string') {
      try {
        parsedItems = JSON.parse(newVal);
      } catch (e) {
        parsedItems = [];
      }
    } else {
      parsedItems = Array.isArray(newVal)
        ? JSON.parse(JSON.stringify(newVal))
        : [];
    }

    // Deep comparison to avoid unnecessary updates
    // Normalize numeric values for comparison
    const normalize = arr =>
      arr.map(item => {
        const normalized = { ...item };
        if (normalized.lat !== undefined)
          normalized.lat = parseFloat(normalized.lat) || normalized.lat;
        if (normalized.lng !== undefined)
          normalized.lng = parseFloat(normalized.lng) || normalized.lng;
        return normalized;
      });
    const currentItemsStr = JSON.stringify(
      normalize(items.value.map(({ _localId, ...rest }) => rest))
    );
    const newItemsStr = JSON.stringify(normalize(parsedItems));
    if (currentItemsStr === newItemsStr) return;

    // Preserve existing _localId when possible to maintain stable keys
    const existingItems = items.value;
    items.value = parsedItems.map((item, idx) => {
      const existing = existingItems.find(
        ex =>
          ex._localId &&
          (ex[props.titleField] === item[props.titleField] ||
            (ex.lat === item.lat && ex.lng === item.lng) ||
            (ex.address === item.address && item.address))
      );
      return {
        ...item,
        _localId: existing?._localId || item._localId || getLocalId(),
      };
    });
  },
  { immediate: true, deep: true }
);

const addItem = () => {
  if (updateTimeout) {
    clearTimeout(updateTimeout);
  }
  const newItem = { _localId: getLocalId() };
  props.fields.forEach(f => {
    newItem[f.key] = f.default !== undefined ? f.default : '';
  });
  items.value.push(newItem);
  // Auto-expand the new item for immediate editing
  expandedIndex.value = items.value.length - 1;
  update();
};

const removeItem = index => {
  if (updateTimeout) {
    clearTimeout(updateTimeout);
  }
  items.value.splice(index, 1);
  update();
};

let updateTimeout = null;

// Geocoding function using Nominatim (OpenStreetMap)
const geocodeAddress = async address => {
  if (!address || address.trim() === '') return null;

  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}&limit=1&addressdetails=1`,
      {
        headers: {
          'User-Agent': 'Chatwoot Dashboard/1.0',
        },
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    if (data && data.length > 0) {
      return {
        lat: parseFloat(data[0].lat),
        lng: parseFloat(data[0].lon),
        displayName: data[0].display_name,
      };
    }
    return null;
  } catch (error) {
    console.error('Geocoding error:', error);
    return null;
  }
};

const updateItem = async (index, key, value) => {
  // Handle address geocoding
  if (key === 'address' && value && value.trim() !== '') {
    items.value[index][key] = value;
    // Show loading state
    const loadingKey = `_geocoding_${index}`;
    items.value[index][loadingKey] = true;

    // Debounce geocoding to avoid too many API calls
    if (updateTimeout) {
      clearTimeout(updateTimeout);
    }
    updateTimeout = setTimeout(async () => {
      const result = await geocodeAddress(value);
      if (result) {
        items.value[index].lat = result.lat;
        items.value[index].lng = result.lng;
        // Optionally update title if empty
        if (!items.value[index].title && result.displayName) {
          items.value[index].title = result.displayName.split(',')[0];
        }
        delete items.value[index][`_geocoding_error_${index}`];
      } else {
        // Show error if geocoding failed
        items.value[index][`_geocoding_error_${index}`] = true;
      }
      delete items.value[index][loadingKey];
      update();
    }, 800); // Longer delay for geocoding
  } else {
    // Store the raw value for coordinates (will be parsed when saving)
    if (key === 'lat' || key === 'lng') {
      items.value[index][key] = value;
    } else {
      items.value[index][key] = value;
    }
    // Debounce updates to prevent focus loss during typing
    if (updateTimeout) {
      clearTimeout(updateTimeout);
    }
    updateTimeout = setTimeout(() => {
      update();
    }, 150);
  }
};

const update = () => {
  isUpdating.value = true;
  // Remove local IDs and internal flags, convert coordinates to numbers before emitting
  const cleanedItems = items.value.map(item => {
    const { _localId, ...rest } = item;
    const cleaned = { ...rest };
    // Remove geocoding flags and error flags
    Object.keys(cleaned).forEach(key => {
      if (key.startsWith('_geocoding_')) {
        delete cleaned[key];
      }
    });
    // Convert lat/lng to numbers if they exist
    if (cleaned.lat !== undefined && cleaned.lat !== '') {
      const latNum = parseFloat(cleaned.lat);
      cleaned.lat = Number.isNaN(latNum) ? cleaned.lat : latNum;
    }
    if (cleaned.lng !== undefined && cleaned.lng !== '') {
      const lngNum = parseFloat(cleaned.lng);
      cleaned.lng = Number.isNaN(lngNum) ? cleaned.lng : lngNum;
    }
    return cleaned;
  });
  emit('update:modelValue', cleanedItems);
  // Reset flag after next tick to allow watch to process
  nextTick(() => {
    isUpdating.value = false;
  });
};

const expandedIndex = ref(null);

const toggleExpand = index => {
  expandedIndex.value = expandedIndex.value === index ? null : index;
};
</script>

<template>
  <div class="space-y-3">
    <div
      v-if="items.length === 0"
      class="p-6 rounded-xl border-2 border-dashed border-n-weak bg-n-alpha-1/50 text-center"
    >
      <span class="i-lucide-map-pin size-8 text-n-slate-9 mb-2 block" />
      <p class="text-sm font-medium text-n-slate-11 mb-1">Aucun marqueur</p>
      <p class="text-xs text-n-slate-9">
        Cliquez sur "Ajouter un marqueur" pour commencer
      </p>
    </div>

    <div
      v-for="(item, index) in items"
      :key="item._localId"
      class="border border-n-weak rounded-xl overflow-hidden bg-n-solid-1 shadow-sm transition-all"
      :class="{
        'ring-1 ring-n-blue-9 border-n-blue-9': expandedIndex === index,
      }"
    >
      <!-- Item Header -->
      <div
        class="flex items-center justify-between p-3 cursor-pointer hover:bg-n-alpha-1"
        @click="toggleExpand(index)"
      >
        <div class="flex items-center gap-3 min-w-0">
          <div class="p-1.5 rounded-lg bg-n-alpha-2 text-n-slate-10">
            <span
              class="size-4"
              :class="[
                item.icon ||
                  (item.address || (item.lat && item.lng)
                    ? 'i-lucide-map-pin'
                    : 'i-lucide-list'),
              ]"
            />
          </div>
          <div class="flex-1 min-w-0">
            <span class="text-sm font-medium text-n-slate-12 truncate block">
              {{
                item[titleField] ||
                (item.address
                  ? item.address.split(',')[0]
                  : item.lat && item.lng
                    ? `${item.lat}, ${item.lng}`
                    : `Élément ${index + 1}`)
              }}
            </span>
            <span
              v-if="item.address"
              class="text-[10px] text-n-slate-9 truncate block"
            >
              {{ item.address }}
            </span>
            <span
              v-else-if="item[titleField] && item.lat && item.lng"
              class="text-[10px] text-n-slate-9 truncate block"
            >
              {{ item.lat }}, {{ item.lng }}
            </span>
          </div>
        </div>
        <div class="flex items-center gap-1">
          <button
            class="p-1.5 rounded-lg hover:bg-n-ruby-2 text-n-slate-9 hover:text-n-ruby-11 transition-colors"
            @click.stop="removeItem(index)"
          >
            <span class="i-lucide-trash-2 size-4" />
          </button>
          <span
            class="size-4 text-n-slate-9 transition-transform"
            :class="[
              expandedIndex === index
                ? 'i-lucide-chevron-up'
                : 'i-lucide-chevron-down',
            ]"
          />
        </div>
      </div>

      <!-- Item Body -->
      <div
        v-if="expandedIndex === index"
        class="p-4 border-t border-n-weak bg-n-alpha-1/30 space-y-4"
      >
        <div
          v-for="field in fields"
          v-show="
            !field.hidden &&
            !(field.key === 'lat' && item.address && item.lat) &&
            !(field.key === 'lng' && item.address && item.lng)
          "
          :key="field.key"
          class="space-y-1.5"
        >
          <label
            class="flex items-center gap-2 text-xs font-semibold text-n-slate-11 uppercase tracking-wider"
          >
            {{ field.label }}
            <span
              v-if="['lat', 'lng'].includes(field.key) && !item.address"
              class="text-[10px] font-normal normal-case text-n-slate-9"
            >
              (requis si pas d'adresse)
            </span>
            <span
              v-else-if="field.key === 'address'"
              class="text-[10px] font-normal normal-case text-n-teal-9"
            >
              (recommandé)
            </span>
          </label>

          <div v-if="field.type === 'select'" class="space-y-1.5">
            <select
              :value="item[field.key] || field.default || ''"
              class="w-full h-9 px-3 text-sm rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-blue-9 focus:border-n-blue-9 transition-all shadow-sm"
              @change="updateItem(index, field.key, $event.target.value)"
            >
              <option
                v-for="option in field.options"
                :key="typeof option === 'object' ? option.value : option"
                :value="typeof option === 'object' ? option.value : option"
              >
                {{ typeof option === 'object' ? option.label : option }}
              </option>
            </select>
            <div
              v-if="field.key === 'style'"
              class="flex items-center gap-2 text-[10px] text-n-slate-9"
            >
              <div
                class="w-3 h-3 rounded-full border-2 border-white dark:border-n-slate-12 shadow-sm"
                :style="{
                  backgroundColor:
                    {
                      primary: '#3860F9',
                      success: '#16A34A',
                      warning: '#FF8C42',
                      danger: '#DC2626',
                      info: '#3B82F6',
                    }[item[field.key] || field.default] || '#9CA3AF',
                }"
              />
              <span>Aperçu de la couleur du marqueur</span>
            </div>
          </div>

          <IconPicker
            v-else-if="field.type === 'icon'"
            :model-value="item[field.key]"
            @update:model-value="updateItem(index, field.key, $event)"
          />

          <div v-else-if="field.type === 'color'" class="space-y-1.5">
            <div class="flex items-center gap-3">
              <div class="relative flex-1">
                <Input
                  :model-value="item[field.key] || field.default || '#3860F9'"
                  :placeholder="field.placeholder"
                  class="shadow-sm pr-12"
                  @update:model-value="updateItem(index, field.key, $event)"
                />
                <input
                  type="color"
                  :value="item[field.key] || field.default || '#3860F9'"
                  class="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded border border-n-weak cursor-pointer"
                  @input="updateItem(index, field.key, $event.target.value)"
                />
              </div>
              <div
                class="w-10 h-10 rounded-lg border-2 border-n-weak shadow-sm"
                :style="{
                  backgroundColor:
                    item[field.key] || field.default || '#3860F9',
                }"
              />
            </div>
          </div>

          <div v-else-if="field.type === 'address'" class="space-y-1.5">
            <div class="relative">
              <Input
                :model-value="item[field.key] || ''"
                :placeholder="field.placeholder"
                class="shadow-sm pr-10"
                @update:model-value="updateItem(index, field.key, $event)"
              />
              <div
                v-if="item[`_geocoding_${index}`]"
                class="absolute right-3 top-1/2 -translate-y-1/2"
              >
                <span
                  class="i-lucide-loader-2 size-4 text-n-brand animate-spin"
                />
              </div>
              <button
                v-else-if="item[field.key] && !item.lat && !item.lng"
                class="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-lg hover:bg-n-alpha-2 text-n-slate-10 hover:text-n-brand transition-colors"
                title="Rechercher l'adresse"
                @click="updateItem(index, field.key, item[field.key])"
              >
                <span class="i-lucide-search size-4" />
              </button>
              <span
                v-else-if="item.lat && item.lng"
                class="absolute right-2 top-1/2 -translate-y-1/2 text-n-teal-9"
                title="Coordonnées trouvées"
              >
                <span class="i-lucide-check-circle size-4" />
              </span>
            </div>
            <p class="text-[10px] text-n-slate-9 italic">
              Saisissez une adresse complète (ex: "123 Rue de Paris, 75001
              Paris, France")
            </p>
            <div
              v-if="item.lat && item.lng"
              class="flex items-center gap-2 text-[10px] text-n-teal-9 font-medium"
            >
              <span class="i-lucide-map-pin size-3" />
              <span
                >Coordonnées: {{ item.lat.toFixed(6) }},
                {{ item.lng.toFixed(6) }}</span
              >
            </div>
            <div
              v-if="item[`_geocoding_error_${index}`] && !item.lat && !item.lng"
              class="flex items-center gap-2 text-[10px] text-n-ruby-9 font-medium"
            >
              <span class="i-lucide-alert-circle size-3" />
              <span
                >Adresse introuvable. Vérifiez l'adresse ou utilisez des
                coordonnées manuelles.</span
              >
            </div>
            <button
              v-if="item.address && item.lat && item.lng"
              class="text-[10px] text-n-slate-9 hover:text-n-slate-11 underline flex items-center gap-1"
              @click="
                item.address = '';
                update();
              "
            >
              <span class="i-lucide-x size-3" />
              Utiliser des coordonnées manuelles
            </button>
          </div>

          <div
            v-else-if="field.type === 'number' && !field.hidden"
            class="space-y-1"
          >
            <div class="relative">
              <Input
                :model-value="item[field.key] || ''"
                type="number"
                :placeholder="field.placeholder"
                :step="field.step || 'any'"
                :min="field.key === 'lat' ? -90 : -180"
                :max="field.key === 'lat' ? 90 : 180"
                class="shadow-sm"
                :class="{
                  'border-n-ruby-9 focus:ring-n-ruby-9':
                    field.key === 'lat' &&
                    item[field.key] &&
                    (parseFloat(item[field.key]) < -90 ||
                      parseFloat(item[field.key]) > 90),
                  'border-n-ruby-9 focus:ring-n-ruby-9':
                    field.key === 'lng' &&
                    item[field.key] &&
                    (parseFloat(item[field.key]) < -180 ||
                      parseFloat(item[field.key]) > 180),
                }"
                @update:model-value="updateItem(index, field.key, $event)"
              />
              <span
                v-if="
                  (field.key === 'lat' &&
                    item[field.key] &&
                    (parseFloat(item[field.key]) < -90 ||
                      parseFloat(item[field.key]) > 90)) ||
                  (field.key === 'lng' &&
                    item[field.key] &&
                    (parseFloat(item[field.key]) < -180 ||
                      parseFloat(item[field.key]) > 180))
                "
                class="absolute right-2 top-1/2 -translate-y-1/2 text-n-ruby-9"
              >
                <span class="i-lucide-alert-circle size-4" />
              </span>
            </div>
            <p class="text-[10px] text-n-slate-9 italic">
              {{
                field.key === 'lat' ? 'Entre -90 et 90' : 'Entre -180 et 180'
              }}
            </p>
            <button
              v-if="
                field.key === 'lat' && !item.address && (!item.lat || !item.lng)
              "
              class="text-[10px] text-n-slate-9 hover:text-n-brand underline flex items-center gap-1"
              @click="
                expandedIndex = index;
                nextTick(() => {
                  const addressField = document.querySelector(
                    `input[placeholder*='${props.fields.find(f => f.type === 'address')?.placeholder || 'adresse'}']`
                  );
                  addressField?.focus();
                });
              "
            >
              <span class="i-lucide-map-pin size-3" />
              Utiliser une adresse postale à la place
            </button>
          </div>
          <Input
            v-else
            :model-value="item[field.key] || ''"
            :placeholder="field.placeholder"
            class="shadow-sm"
            @update:model-value="updateItem(index, field.key, $event)"
          />
        </div>
      </div>
    </div>

    <button
      class="w-full flex flex-col items-center gap-1.5 py-4 rounded-xl border-2 border-dashed border-n-weak hover:border-n-brand hover:bg-n-alpha-1 text-n-slate-10 hover:text-n-brand transition-all group/add-btn"
      @click="addItem"
    >
      <span
        class="i-lucide-plus size-5 transition-transform group-hover/add-btn:scale-110"
      />
      <span class="text-[10px] font-black uppercase tracking-[0.2em]">{{
        addButtonLabel
      }}</span>
    </button>
  </div>
</template>
